import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Phone, Mail, Search } from "lucide-react";
import Avatar from "@/components/Avatar";

export default function Contacts({ embedded = false }) {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/contacts").then((r) => setContacts(r.data));
    api.get("/users").then((r) => setMembers(r.data));
  }, []);

  const memberById = Object.fromEntries(members.map((m) => [m.id, m]));

  const filtered = contacts.filter((c) => {
    const s = q.toLowerCase();
    return !s || c.name.toLowerCase().includes(s) || (c.role || "").toLowerCase().includes(s);
  });

  return (
    <div className={embedded ? "space-y-4" : "space-y-6 animate-fade-up"} data-testid="contacts-page">
      {!embedded && (
        <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Company</div>
            <h1 className="mt-1 font-display text-4xl tracking-tight font-light">Contact List</h1>
            <p className="mt-1 text-zinc-400 text-sm">Reach out to cast, crew, and staff.</p>
          </div>
          <div className="relative w-full md:w-80">
            <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="contact-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search contacts"
              className="bg-[#121212] border-white/10 pl-9"
            />
          </div>
        </header>
      )}
      {embedded && (
        <div className="relative w-full md:w-80">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="contact-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search directory"
            className="bg-[#121212] border-white/10 pl-9"
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((c) => {
          const linkedUser = c.user_id ? memberById[c.user_id] : null;
          return (
          <div key={c.id} className="rounded-lg border border-white/10 bg-[#121212] p-5 hover:border-white/20 transition-colors">
            <div className="flex items-center gap-3">
              <Avatar user={linkedUser || { name: c.name }} size="w-11 h-11" textClass="text-lg" refresh={linkedUser?.avatar_path || 0} />
              <div className="min-w-0">
                <div className="text-white truncate">{c.name}</div>
                <div className="text-xs text-zinc-500">{c.role}</div>
              </div>
            </div>
            <div className="mt-4 space-y-2 text-sm">
              {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-2 text-zinc-300 hover:text-gold"><Phone className="w-3.5 h-3.5" />{c.phone}</a>}
              {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-zinc-300 hover:text-gold"><Mail className="w-3.5 h-3.5" />{c.email}</a>}
            </div>
          </div>
          );
        })}
        {filtered.length === 0 && <div className="col-span-full text-sm text-zinc-500 border border-dashed border-white/10 rounded-md p-8 text-center">No contacts.</div>}
      </div>
    </div>
  );
}
